/**
 * Inline diamond info popup
 *
 * Opens where you click a candidate diamond on the chart and shows what that
 * bar is: its score and grade, the body/ATR numbers behind the score, the
 * conditions the rule rejected it on, and the current call on it.
 *
 * Read-only by design. Labelling lives in the Diamonds side panel
 * (CandidateReviewPanel), which is the single place a verdict and its reason
 * are recorded — this popup no longer duplicates that.
 *
 * Positioned against the chart container and flipped when it would run off an
 * edge, so a candidate near the right side or the bottom still gets a usable
 * popup.
 */

import React, { useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { useCandidateStore } from '../store/candidateStore';
import {
  ReviewCandidate,
  AutoTag,
  describeCandidate,
  readV2Breakdown,
  verdictForGrade,
} from '../utils/setupCandidates';
import './CandidateQuickLabel.css';

interface CandidateQuickLabelProps {
  candidate: ReviewCandidate;
  /** Click position within the chart container. */
  x: number;
  y: number;
  /** Container size, so the popup can flip rather than overflow. */
  containerWidth: number;
  containerHeight: number;
  /** Suggestion for this candidate, when it has no hand label yet. */
  autoTag?: AutoTag | null;
  onClose: () => void;
}

const WIDTH = 250;
// Read-only card: header, metrics, and at most a rejection line and an auto
// row. Only used to decide which way to flip near an edge.
const EST_HEIGHT = 130;
/**
 * Same, with the score breakdown open: a title, four component rows and the
 * total. Was 232 when there were five component rows — the consecutive dock
 * went with the engine's, and an estimate one row too tall flips the popup away
 * from an edge it would have fitted against.
 */
const EST_HEIGHT_WITH_BREAKDOWN = 217;

/** A contribution, explicitly signed — the point is that these add up. */
const added = (n: number): string => `+${n.toFixed(1)}`;

/**
 * A deduction, shown negative.
 *
 * Zero prints bare rather than as "-0.0": negating 0 gives -0, which formats
 * with a sign and reads as a dock that landed when none did.
 */
const deducted = (n: number): string => (n > 0 ? `-${n.toFixed(1)}` : '0.0');

const CandidateQuickLabel: React.FC<CandidateQuickLabelProps> = ({
  candidate,
  x,
  y,
  containerWidth,
  containerHeight,
  autoTag,
  onClose,
}) => {
  const labels = useCandidateStore((s) => s.labels);
  const ref = useRef<HTMLDivElement | null>(null);

  const existing = labels[candidate.id];

  /**
   * How the ENGINE built this bar's score, read straight off its own payload.
   *
   * There is nothing else it could be. The candidate handed in is built from
   * the engine's diamond (or its rejection) and carries no locally derived
   * number — the popup used to prefer the engine's components and fall back to
   * a set computed here, with a line of small print saying which of the two you
   * were looking at. Both the fallback and the small print are gone.
   *
   * Null on a bar you hand-labelled `missed`: the engine rejected it, so there
   * is no score and no breakdown, and the rejection line below is the answer.
   */
  const parts = readV2Breakdown(candidate);

  // Escape closes; clicking outside closes. Both bound while open only.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    const onDown = (e: MouseEvent): void => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener('keydown', onKey);
    // Defer: the click that opened this popup would otherwise close it.
    const t = setTimeout(() => document.addEventListener('mousedown', onDown), 0);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
      clearTimeout(t);
    };
  }, [onClose]);

  // Flip away from whichever edge we're near.
  const estHeight = parts ? EST_HEIGHT_WITH_BREAKDOWN : EST_HEIGHT;
  const left = x + WIDTH + 16 > containerWidth ? Math.max(4, x - WIDTH - 12) : x + 12;
  const top = y + estHeight + 16 > containerHeight ? Math.max(4, y - estHeight - 8) : y + 8;

  const when = (() => {
    const d = new Date(candidate.timestamp);
    return `${d.toLocaleDateString([], { month: 'short', day: 'numeric' })}, ${d.toLocaleTimeString(
      [],
      { hour: '2-digit', minute: '2-digit' }
    )}`;
  })();

  return (
    <div className="cql-popup" style={{ left, top, width: WIDTH }} ref={ref}>
      <div className="cql-head">
        <span className={`cql-dir ${candidate.direction ?? 'none'}`}>
          {(candidate.direction ?? '—').toUpperCase()}
        </span>
        <span className="cql-time">{when}</span>
        {existing && <span className={`cql-tag ${existing.verdict}`}>{existing.verdict}</span>}
        <button className="cql-close" onClick={onClose} title="Close">
          <X size={11} />
        </button>
      </div>

      <div className="cql-metrics">
        {/* One decimal, matching the breakdown's total row. The raw v2 score
            is an unrounded float, so printing it bare put a 17-digit number
            in the header and made "do the parts add up to this" impossible to
            check by eye.

            A rejected bar has no score and says so rather than showing a
            number: that is the whole point of deleting the second scorer. */}
        {candidate.score !== null && candidate.grade !== null ? (
          <span className={`cql-score ${candidate.grade}`}>
            {candidate.score.toFixed(1)} · {candidate.grade}
          </span>
        ) : (
          <span className="cql-score g-none">not scored — rule rejected it</span>
        )}
        {candidate.childBodyPctOfParentBody !== null && (
          <span>child/parent body {candidate.childBodyPctOfParentBody.toFixed(0)}%</span>
        )}
        {candidate.parentBodyPct !== null && (
          <span>parent body {candidate.parentBodyPct.toFixed(0)}%</span>
        )}
        {candidate.atrRatio !== null && (
          <span>{candidate.atrRatio.toFixed(2)}&times;ATR</span>
        )}
      </div>

      {/*
        How the score was built.

        Laid out as a two-column ledger rather than a sentence: the whole point
        is that the right-hand column adds up, and a reader checking that needs
        the numbers stacked and aligned. Each row is a bare label and a number —
        the raw child/parent/atr measurements are already on the metrics line
        above, so repeating them here would double the height to say nothing new.

        The hod/lod dock is shown even at zero, because "it was not docked" and
        "docking was not considered" are different answers and only one of them
        is true.

        There was a second dock row, `consecutive`. The engine no longer applies
        one: the live bar path built its trailing vector as
        `repeat(None).take(len-1)`, so the signal window was always empty and the
        dock was always 0.0 on every diamond the bot actually traded. The row
        printed a permanent "0.0" that looked like a considered answer.
      */}
      {parts && (
        <div className="cql-breakdown">
          {/*
            One scorer, so the title is unconditional again. It used to branch
            on whether an engine diamond existed, because the alternative was a
            local estimate; there is no alternative now.
          */}
          <div className="cql-bd-title">how the engine scored it</div>
          <div className="cql-bd-row">
            <span>child body</span>
            <b className="add">{added(parts.childPoints)}</b>
          </div>
          <div className="cql-bd-row">
            <span>parent body</span>
            <b className="add">{added(parts.parentPoints)}</b>
          </div>
          <div className="cql-bd-row">
            <span>atr</span>
            <b className="add">{added(parts.atrPoints)}</b>
          </div>
          <div className={`cql-bd-row ${parts.hodLodDock > 0 ? '' : 'nil'}`}>
            <span>hod/lod</span>
            <b className="sub">{deducted(parts.hodLodDock)}</b>
          </div>
          <div className={`cql-bd-row ${parts.consecutiveDock > 0 ? '' : 'nil'}`}>
            <span>consecutive</span>
            <b className="sub">{deducted(parts.consecutiveDock)}</b>
          </div>
          {/*
            `total` is the engine's `score`, verbatim — not the four rows
            re-added. Re-adding them was a reimplementation of the scorer's last
            line, and the two "does this match" warnings that used to sit here
            existed only because a second scorer did. Both are gone with it; the
            engine pins the identity in its own tests.
          */}
          <div className="cql-bd-row total">
            <span>total</span>
            <b>{parts.total.toFixed(1)}</b>
          </div>
        </div>
      )}

      {candidate.rejection && (
        <div className="cql-fails">rule rejected it: {candidate.rejection.detail}</div>
      )}

      {!existing && !autoTag && candidate.grade !== null && (
        <div className="cql-auto">
          <span className={`cql-tag ${verdictForGrade(candidate.grade)}`}>
            auto {candidate.grade}
          </span>
          {/* What the bar IS, not what it scored — the number is already on
              the line above, and repeating it explains nothing. */}
          <span className="cql-auto-reason">{describeCandidate(candidate)}</span>
        </div>
      )}

      {!existing && autoTag && (
        <div className="cql-auto">
          <span className={`cql-tag ${autoTag.verdict}`}>
            auto {autoTag.verdict} {Math.round(autoTag.confidence * 100)}%
          </span>
          {autoTag.reason && <span className="cql-auto-reason">“{autoTag.reason}”</span>}
        </div>
      )}
    </div>
  );
};

export default CandidateQuickLabel;
